import { useEffect, useMemo, useRef, useState } from "react";
import { Send, Trash2 } from "lucide-react";

import {
  GLOBAL_CONVERSATION_ID,
  clearDirectConversation,
  getConversationId,
  markConversationMessagesRead,
  markConversationRead,
  sendGlobalMessage,
  sendMessage,
  subscribeConversationMessages,
  subscribeGlobalConversation,
  subscribeUserConversations,
  toggleMessageReaction,
  uploadChatAttachment
} from "../services/chatService";
import { addLog } from "../services/logService";

import { isPresenceOnline } from "../services/presenceService";

const GLOBAL_USER_ID = "__tum_kullanicilar__";
const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🙏"];
const COMPOSER_EMOJIS = ["👍", "❤️", "😂", "🙂", "🙏", "🎉", "✅", "🔥"];
const MAX_CHAT_ATTACHMENTS = 5;
const MAX_CHAT_ATTACHMENT_SIZE = 10 * 1024 * 1024;

const formatMessageTime = (value) => {
  if (!value) return "";

  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

function MessagesView({
  currentUser,
  users = [],
  presenceUsers = [],
  themeMode = "light",
  messageFontSize = 14,
  compact = false
}) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [conversations, setConversations] = useState([]);
  const [globalConversation, setGlobalConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [reactionMessageId, setReactionMessageId] = useState("");
  const [forwardingMessage, setForwardingMessage] = useState(null);
  const [forwardTargetId, setForwardTargetId] = useState("");
  const [actionMenuMessageId, setActionMenuMessageId] = useState("");
  const [isComposerEmojiOpen, setIsComposerEmojiOpen] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [isClearingConversation, setIsClearingConversation] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const isDark = themeMode === "dark";
  const currentUid = currentUser?.uid || currentUser?.id || "";
  const canClearConversations = String(currentUser?.role || "").toLowerCase() === "admin";

  const presenceByUid = useMemo(() => {
    return presenceUsers.reduce((acc, item) => {
      acc[item.uid || item.id] = item;
      return acc;
    }, {});
  }, [presenceUsers]);

  const userByUid = useMemo(() => {
    return users.reduce((acc, item) => {
      const uid = item.uid || item.id;

      if (uid) {
        acc[uid] = item;
      }

      return acc;
    }, {});
  }, [users]);

  const directoryUsers = useMemo(() => {
    return users
      .map((item) => ({
        ...item,
        uid: item.uid || item.id
      }))
      .filter((item) => item.uid && item.uid !== currentUid && item.isActive !== false)
      .sort((first, second) => {
        const firstOnline = isPresenceOnline(presenceByUid[first.uid]) ? 1 : 0;
        const secondOnline = isPresenceOnline(presenceByUid[second.uid]) ? 1 : 0;

        if (firstOnline !== secondOnline) return secondOnline - firstOnline;

        return String(first.name || first.email || "").localeCompare(
          String(second.name || second.email || ""),
          "tr-TR"
        );
      });
  }, [currentUid, presenceByUid, users]);

  const isGlobalSelected = selectedUserId === GLOBAL_USER_ID;
  const selectedUser = isGlobalSelected
    ? {
        uid: GLOBAL_USER_ID,
        name: "Tüm Kullanıcılar",
        email: "Toplu mesajlaşma kanalı"
      }
    : directoryUsers.find((item) => item.uid === selectedUserId);
  const selectedConversationId =
    isGlobalSelected
      ? GLOBAL_CONVERSATION_ID
      : currentUid && selectedUserId
      ? getConversationId(currentUid, selectedUserId)
      : "";
  const selectedConversation = isGlobalSelected
    ? globalConversation
    : conversations.find((conversation) => conversation.id === selectedConversationId);
  const forwardTargets = useMemo(() => [
    {
      uid: GLOBAL_USER_ID,
      name: "Tüm Kullanıcılar",
      email: "Toplu mesajlaşma kanalı"
    },
    ...directoryUsers
  ], [directoryUsers]);

  useEffect(() => {
    if (!currentUid) return undefined;

    return subscribeUserConversations(currentUid, setConversations);
  }, [currentUid]);

  useEffect(() => {
    if (!currentUid) return undefined;

    return subscribeGlobalConversation(setGlobalConversation);
  }, [currentUid]);

  useEffect(() => {
    const shouldLoadConversationMessages =
      isGlobalSelected || Boolean(selectedConversation);

    if (!selectedConversationId || !shouldLoadConversationMessages) {
      setMessages([]);
      setReplyingToMessage(null);
      setReactionMessageId("");
      setActionMenuMessageId("");
      setIsComposerEmojiOpen(false);
      clearSelectedAttachments();
      setForwardingMessage(null);
      setForwardTargetId("");
      return undefined;
    }

    let isActiveSubscription = true;

    setMessages([]);

    const unsubscribe = subscribeConversationMessages(
      selectedConversationId,
      (nextMessages) => {
        if (isActiveSubscription) {
          setMessages(nextMessages);
        }
      }
    );

    return () => {
      isActiveSubscription = false;
      unsubscribe?.();
    };
  }, [isGlobalSelected, selectedConversation, selectedConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!selectedConversation || !currentUid) return;

    const hasDirectUnread =
      Array.isArray(selectedConversation.unreadBy) &&
      selectedConversation.unreadBy.includes(currentUid);
    const hasGlobalUnread =
      selectedConversation.type === "global" &&
      selectedConversation.lastSenderId !== currentUid &&
      !(
        Array.isArray(selectedConversation.readBy) &&
        selectedConversation.readBy.includes(currentUid)
      );

    if (hasDirectUnread || hasGlobalUnread) {
      markConversationRead({
        conversation: selectedConversation,
        uid: currentUid
      }).catch((error) => {
        console.error("Mesaj okundu bilgisi güncellenemedi:", error);
      });
    }
  }, [currentUid, selectedConversation]);

  useEffect(() => {
    if (!selectedConversationId || !currentUid || messages.length === 0) return;

    markConversationMessagesRead({
      conversationId: selectedConversationId,
      messages,
      uid: currentUid
    }).catch((error) => {
      console.error("Mesaj okundu bilgisi güncellenemedi:", error);
    });
  }, [currentUid, messages, selectedConversationId]);

  const conversationByUserId = useMemo(() => {
    return conversations.reduce((acc, conversation) => {
      const otherUserId = (conversation.participants || []).find(
        (participantId) => participantId !== currentUid
      );

      if (otherUserId) {
        acc[otherUserId] = conversation;
      }

      return acc;
    }, {});
  }, [conversations, currentUid]);

  const hasGlobalUnread =
    Boolean(globalConversation?.lastMessage) &&
    globalConversation.lastSenderId !== currentUid &&
    !(
      Array.isArray(globalConversation.readBy) &&
      globalConversation.readBy.includes(currentUid)
    );

  const focusMessageInput = () => {
    window.requestAnimationFrame(() => {
      messageInputRef.current?.focus();
      window.setTimeout(() => {
        messageInputRef.current?.focus();
      }, 80);
    });
  };

  const clearSelectedAttachments = () => {
    setSelectedAttachments((current) => {
      current.forEach((attachment) => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });

      return [];
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAttachmentSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setSelectedAttachments((current) => {
      const availableCount = Math.max(MAX_CHAT_ATTACHMENTS - current.length, 0);
      const acceptedFiles = files
        .filter((file) => {
          if (file.size > MAX_CHAT_ATTACHMENT_SIZE) {
            alert(`${file.name} 10 MB sınırını aşıyor.`);
            return false;
          }

          return true;
        })
        .slice(0, availableCount);

      if (files.length > availableCount) {
        alert(`Bir mesajda en fazla ${MAX_CHAT_ATTACHMENTS} ek gönderebilirsiniz.`);
      }

      return [
        ...current,
        ...acceptedFiles.map((file) => ({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          file,
          name: file.name,
          type: file.type || "application/octet-stream",
          size: file.size,
          isImage: String(file.type || "").startsWith("image/"),
          previewUrl: String(file.type || "").startsWith("image/") ? URL.createObjectURL(file) : ""
        }))
      ];
    });

    event.target.value = "";
  };

  const removeSelectedAttachment = (attachmentId) => {
    setSelectedAttachments((current) => {
      const target = current.find((attachment) => attachment.id === attachmentId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }

      return current.filter((attachment) => attachment.id !== attachmentId);
    });
  };

  const formatFileSize = (size = 0) => {
    const numericSize = Number(size || 0);
    if (numericSize >= 1024 * 1024) return `${(numericSize / (1024 * 1024)).toFixed(1)} MB`;
    if (numericSize >= 1024) return `${Math.round(numericSize / 1024)} KB`;
    return `${numericSize} B`;
  };

  useEffect(() => {
    if (selectedUserId) {
      focusMessageInput();
    }
  }, [selectedUserId]);

  const handleSendMessage = async (event) => {
    event.preventDefault();

    const textToSend = messageText.trim();
    const attachmentsToSend = selectedAttachments;

    if (!selectedUser || (!textToSend && attachmentsToSend.length === 0)) return;

    setIsSending(true);

    try {
      const uploadedAttachments = [];

      for (const attachment of attachmentsToSend) {
        uploadedAttachments.push(
          await uploadChatAttachment({
            conversationId: selectedConversationId,
            currentUser,
            file: attachment.file
          })
        );
      }

      if (isGlobalSelected) {
        await sendGlobalMessage({
          currentUser,
          text: textToSend,
          attachments: uploadedAttachments,
          replyTo: replyingToMessage
        });
      } else {
        await sendMessage({
          currentUser,
          targetUser: selectedUser,
          text: textToSend,
          attachments: uploadedAttachments,
          replyTo: replyingToMessage
        });
      }

      setMessageText("");
      setReplyingToMessage(null);
      setIsComposerEmojiOpen(false);
      clearSelectedAttachments();
    } catch (error) {
      console.error("Mesaj gönderilemedi:", error);
      alert("Mesaj gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setIsSending(false);
      focusMessageInput();
    }
  };

  const handleToggleReaction = async (message, emoji) => {
    if (!selectedConversationId) return;

    try {
      await toggleMessageReaction({
        conversationId: selectedConversationId,
        message,
        emoji,
        currentUser
      });
      setReactionMessageId("");
      setActionMenuMessageId("");
    } catch (error) {
      console.error("İfade eklenemedi:", error);
      alert("İfade eklenemedi. Lütfen tekrar deneyin.");
    }
  };

  const startReply = (message) => {
    setReplyingToMessage(message);
    setActionMenuMessageId("");
    focusMessageInput();
  };

  const startForward = (message) => {
    setForwardingMessage(message);
    setForwardTargetId("");
    setActionMenuMessageId("");
  };

  const appendEmojiToComposer = (emoji) => {
    setMessageText((current) => `${current}${emoji}`);
    setIsComposerEmojiOpen(false);
    focusMessageInput();
  };

  const copyMessageText = async (message) => {
    try {
      await navigator.clipboard.writeText(message?.text || "");
      setActionMenuMessageId("");
    } catch (error) {
      console.error("Mesaj kopyalanamadı:", error);
      alert("Mesaj kopyalanamadı.");
    }
  };

  const handleForwardMessage = async () => {
    if (!forwardingMessage || !forwardTargetId) return;

    const target = forwardTargets.find((item) => item.uid === forwardTargetId);
    if (!target) return;

    setIsSending(true);

    try {
      const forwardedFrom = {
        id: forwardingMessage.id,
        text: forwardingMessage.text,
        senderId: forwardingMessage.senderId,
        senderName: forwardingMessage.senderName,
        senderEmail: forwardingMessage.senderEmail
      };

      if (forwardTargetId === GLOBAL_USER_ID) {
        await sendGlobalMessage({
          currentUser,
          text: forwardingMessage.text,
          forwardedFrom
        });
      } else {
        await sendMessage({
          currentUser,
          targetUser: target,
          text: forwardingMessage.text,
          forwardedFrom
        });
      }

      setForwardingMessage(null);
      setForwardTargetId("");
    } catch (error) {
      console.error("Mesaj iletilemedi:", error);
      alert("Mesaj iletilemedi. Lütfen tekrar deneyin.");
    } finally {
      setIsSending(false);
      focusMessageInput();
    }
  };

  const closeFloatingMessageControls = () => {
    setActionMenuMessageId("");
    setReactionMessageId("");
    setIsComposerEmojiOpen(false);
  };

  const clearSelectedConversation = async () => {
    if (
      !canClearConversations ||
      isGlobalSelected ||
      !selectedConversationId ||
      !selectedConversation ||
      !selectedUser ||
      isClearingConversation
    ) return;

    const targetName = selectedUser.name || selectedUser.email || "seçili kullanıcı";
    const confirmed = await window.appConfirm(
      `${targetName} ile yapılan tüm yazışmalar ve sohbet ekleri kalıcı olarak silinecek. Bu işlem iki tarafın ekranından da konuşmayı kaldırır ve geri alınamaz. Devam edilsin mi?`,
      {
        title: "Sohbet kalıcı olarak temizlensin mi?",
        type: "danger",
        confirmText: "Sohbeti Temizle",
        cancelText: "Vazgeç"
      }
    );
    if (!confirmed) return;

    setIsClearingConversation(true);
    try {
      const result = await clearDirectConversation({ conversationId: selectedConversationId });
      await addLog({
        action: "SOHBET_TEMIZLENDI",
        detail: `${targetName} (${selectedUser.email || selectedUser.uid}) ile konuşma temizlendi · ${result.deletedMessageCount} mesaj`,
        userEmail: currentUser?.email || ""
      });
      setMessages([]);
      setReplyingToMessage(null);
      setForwardingMessage(null);
      setReactionMessageId("");
      setActionMenuMessageId("");
      clearSelectedAttachments();
      await window.appAlert(`${targetName} ile olan sohbet temizlendi.`, {
        title: "Sohbet temizlendi",
        type: "success"
      });
    } catch (error) {
      console.error("Sohbet temizlenemedi:", error);
      await window.appAlert(`Sohbet temizlenemedi: ${error.message}`, {
        title: "İşlem tamamlanamadı",
        type: "error"
      });
    } finally {
      setIsClearingConversation(false);
    }
  };

  return (
    <div
      style={{ height: compact ? "100%" : "auto" }}
      onClick={(event) => {
        if (!event.target.closest?.("[data-chat-control='true']")) {
          closeFloatingMessageControls();
        }
      }}
    >
      {!compact && <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 24 }}>Mesajlar</h2>
        <p
          style={{
            margin: "6px 0 0",
            color: isDark ? "#9fb0c7" : "#64748b",
            fontWeight: 600
          }}
        >
          Aktif kullanıcıları görün ve ekip içinde hızlıca yazışın.
        </p>
      </div>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "232px minmax(0, 1fr)" : "280px minmax(0, 1fr)",
          gap: compact ? 12 : 16,
          minHeight: compact ? "100%" : 620,
          height: compact ? "100%" : "auto"
        }}
      >
        <div
          style={{
            background: isDark ? "#162235" : "#fff",
            border: `1px solid ${isDark ? "#27364d" : "#e2e8f0"}`,
            borderRadius: 18,
            padding: compact ? 10 : 12,
            boxShadow: isDark ? "none" : "0 12px 30px rgba(15,23,42,0.06)",
            overflow: "hidden"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: compact ? 8 : 10,
              padding: compact ? "2px 2px 8px" : "4px 4px 10px",
              borderBottom: `1px solid ${isDark ? "#27364d" : "#e2e8f0"}`
            }}
          >
            <strong style={{ fontSize: compact ? 12 : 14 }}>Kanallar</strong>
            <span
              style={{
                padding: compact ? "3px 7px" : "5px 9px",
                borderRadius: 999,
                background: isDark ? "#0f172a" : "#e0f2fe",
                color: isDark ? "#bae6fd" : "#0369a1",
                fontSize: compact ? 10 : 12,
                fontWeight: 900
              }}
            >
              {directoryUsers.filter((item) => isPresenceOnline(presenceByUid[item.uid])).length} çevrimiçi
            </span>
          </div>

          <div style={{ maxHeight: compact ? "calc(100vh - 250px)" : 540, overflowY: "auto", paddingRight: 3 }}>
            <button
              type="button"
              onClick={() => {
                setSelectedUserId(GLOBAL_USER_ID);
                focusMessageInput();
              }}
              style={{
                width: "100%",
                border: `1px solid ${
                  isGlobalSelected ? "#38bdf8" : isDark ? "#27364d" : "#bae6fd"
                }`,
                background: isGlobalSelected
                  ? isDark
                    ? "#0f3148"
                    : "#e0f2fe"
                  : isDark
                  ? "#0f2235"
                  : "#f0f9ff",
                color: isDark ? "#e5edf6" : "#0f172a",
                borderRadius: 14,
                padding: compact ? "8px 9px" : "10px 10px",
                marginBottom: compact ? 8 : 10,
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                gap: 8,
                alignItems: "center"
              }}
            >
              <span
                style={{
                  width: compact ? 30 : 34,
                  height: compact ? 30 : 34,
                  borderRadius: "50%",
                  background: isDark ? "#164e63" : "#dbeafe",
                  color: isDark ? "#cffafe" : "#1d4ed8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: compact ? 13 : 15,
                  fontWeight: 900,
                  flexShrink: 0
                }}
              >
                👥
              </span>

              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontWeight: 900,
                    fontSize: compact ? 12 : 13
                  }}
                >
                  <span>Tüm Kullanıcılar</span>
                  {hasGlobalUnread && (
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: "#ef4444",
                        flexShrink: 0
                      }}
                    />
                  )}
                </span>

                <span
                  style={{
                    display: "block",
                    marginTop: 4,
                    color: isDark ? "#bae6fd" : "#0369a1",
                    fontSize: compact ? 10 : 12,
                    fontWeight: 800
                  }}
                >
                  Toplu mesajlaşma kanalı
                </span>
              </span>
            </button>

            {directoryUsers.map((directoryUser) => {
              const presence = presenceByUid[directoryUser.uid];
              const isOnline = isPresenceOnline(presence);
              const photoURL =
                directoryUser.photoURL ||
                directoryUser.avatarURL ||
                directoryUser.avatarUrl ||
                directoryUser.profilePhotoURL ||
                "";
              const conversation = conversationByUserId[directoryUser.uid];
              const hasUnread =
                Array.isArray(conversation?.unreadBy) &&
                conversation.unreadBy.includes(currentUid);

              return (
                <button
                  key={directoryUser.uid}
                  type="button"
                  onClick={() => {
                    setSelectedUserId(directoryUser.uid);
                    focusMessageInput();
                  }}
                  style={{
                    width: "100%",
                    border: `1px solid ${
                      selectedUserId === directoryUser.uid
                        ? "#38bdf8"
                        : isDark
                        ? "#27364d"
                        : "#e2e8f0"
                    }`,
                    background:
                      selectedUserId === directoryUser.uid
                        ? isDark
                          ? "#0f3148"
                          : "#e0f2fe"
                        : isDark
                        ? "#111c2d"
                        : "#fff",
                    color: isDark ? "#e5edf6" : "#0f172a",
                    borderRadius: 14,
                    padding: compact ? "8px 9px" : "10px 10px",
                    marginBottom: compact ? 7 : 9,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    gap: 8,
                    alignItems: "center"
                  }}
                >
                  <span
                    style={{
                      position: "relative",
                      width: compact ? 30 : 34,
                      height: compact ? 30 : 34,
                      borderRadius: "50%",
                      background: isOnline ? "#dcfce7" : isDark ? "#1e293b" : "#f1f5f9",
                      color: isOnline ? "#15803d" : isDark ? "#cbd5e1" : "#475569",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: compact ? 12 : 14,
                      fontWeight: 900,
                      flexShrink: 0
                    }}
                  >
                    {photoURL ? (
                      <img
                        src={photoURL}
                        alt={directoryUser.name || directoryUser.email || "Kullanıcı"}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          objectFit: "cover"
                        }}
                      />
                    ) : (
                      (directoryUser.name || directoryUser.email || "?")
                        .slice(0, 1)
                        .toLocaleUpperCase("tr-TR")
                    )}
                    <span
                      title={isOnline ? "Çevrimiçi" : "Çevrimdışı"}
                      style={{
                        position: "absolute",
                        right: -1,
                        bottom: -1,
                        width: compact ? 10 : 12,
                        height: compact ? 10 : 12,
                        borderRadius: "50%",
                        background: isOnline ? "#22c55e" : "#94a3b8",
                        border: `2px solid ${isDark ? "#111c2d" : "#fff"}`
                      }}
                    />
                  </span>

                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        fontWeight: 900,
                        fontSize: compact ? 12 : 13
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {directoryUser.name || directoryUser.email}
                      </span>
                      {hasUnread && (
                        <span
                          style={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            background: "#ef4444",
                            flexShrink: 0
                          }}
                        />
                      )}
                    </span>

                    <span
                      style={{
                        display: "block",
                        marginTop: 5,
                        color: isDark ? "#b6c4d8" : "#475569",
                        fontSize: compact ? 10 : 11,
                        fontWeight: 800,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis"
                      }}
                      title={directoryUser.email}
                    >
                      {directoryUser.email || "E-posta bilgisi yok"}
                    </span>

                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: compact ? 4 : 5,
                        color: isOnline ? "#16a34a" : isDark ? "#94a3b8" : "#64748b",
                        fontSize: compact ? 10 : 11,
                        fontWeight: 900
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: isOnline ? "#22c55e" : "#94a3b8"
                        }}
                      />
                      {isOnline ? "Çevrimiçi" : "Çevrimdışı"}
                    </span>
                  </span>
                </button>
              );
            })}

            {directoryUsers.length === 0 && (
              <div
                style={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  padding: 12,
                  fontWeight: 700
                }}
              >
                Listelenecek kullanıcı bulunamadı.
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            background: isDark ? "#162235" : "#fff",
            border: `1px solid ${isDark ? "#27364d" : "#e2e8f0"}`,
            borderRadius: 20,
            boxShadow: isDark ? "none" : "0 12px 30px rgba(15,23,42,0.06)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          <div
            style={{
              padding: "18px 20px",
              borderBottom: `1px solid ${isDark ? "#27364d" : "#e2e8f0"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <div style={{ minWidth: 0 }}>
              <strong style={{ fontSize: 17 }}>
                {selectedUser ? selectedUser.name || selectedUser.email : "Bir kullanıcı seçin"}
              </strong>
              {selectedUser && (
                <>
                  <div
                    style={{
                      marginTop: 4,
                      color: isDark ? "#b6c4d8" : "#475569",
                      fontSize: 13,
                      fontWeight: 800
                    }}
                  >
                    {isGlobalSelected
                      ? "Tüm aktif kullanıcıların ortak mesaj alanı"
                      : selectedUser.email || "E-posta bilgisi yok"}
                  </div>

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      marginTop: 6,
                      padding: "4px 9px",
                      borderRadius: 999,
                      background: isGlobalSelected
                        ? isDark
                          ? "#0f3148"
                          : "#e0f2fe"
                        : isPresenceOnline(presenceByUid[selectedUser.uid])
                        ? "#dcfce7"
                        : isDark
                        ? "#1e293b"
                        : "#f1f5f9",
                      color: isGlobalSelected
                        ? isDark
                          ? "#bae6fd"
                          : "#0369a1"
                        : isPresenceOnline(presenceByUid[selectedUser.uid])
                        ? "#166534"
                        : isDark
                        ? "#cbd5e1"
                        : "#64748b",
                      fontSize: 12,
                      fontWeight: 950
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: isGlobalSelected
                          ? "#38bdf8"
                          : isPresenceOnline(presenceByUid[selectedUser.uid])
                          ? "#22c55e"
                          : "#94a3b8"
                      }}
                    />
                    {isGlobalSelected
                      ? "Toplu kanal"
                      : isPresenceOnline(presenceByUid[selectedUser.uid])
                      ? "Çevrimiçi"
                      : "Şu an çevrimdışı"}
                  </div>
                </>
              )}
            </div>
            {canClearConversations && selectedUser && !isGlobalSelected && selectedConversation && (
              <button
                type="button"
                onClick={clearSelectedConversation}
                disabled={isClearingConversation}
                title="Bu kullanıcıyla olan sohbeti kalıcı olarak temizle"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  minHeight: 38,
                  padding: "0 11px",
                  flexShrink: 0,
                  borderRadius: 11,
                  border: `1px solid ${isDark ? "#7f1d1d" : "#fecaca"}`,
                  background: isDark ? "#3f161b" : "#fff1f2",
                  color: isDark ? "#fda4af" : "#be123c",
                  fontWeight: 900,
                  fontSize: 12,
                  cursor: isClearingConversation ? "wait" : "pointer",
                  opacity: isClearingConversation ? 0.65 : 1
                }}
              >
                <Trash2 size={15} />
                {isClearingConversation ? "Temizleniyor…" : "Sohbeti temizle"}
              </button>
            )}
          </div>

          <div
            style={{
              flex: 1,
              padding: 20,
              overflowY: "auto",
              background: isDark ? "#101827" : "#f8fafc"
            }}
          >
            {!selectedUser && (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontWeight: 800
                }}
              >
                Yazışmaya başlamak için soldan bir kullanıcı seçin.
              </div>
            )}

            {selectedUser && messages.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontWeight: 800,
                  marginTop: 80
                }}
              >
                Henüz mesaj yok. İlk mesajı siz yazabilirsiniz.
              </div>
            )}

            {selectedUser && messages.map((message, messageIndex) => {
              const isMine = message.senderId === currentUid;
              const shouldOpenMenuUpward = messageIndex >= messages.length - 3;
              const senderUser = userByUid[message.senderId] || {};
              const senderName =
                isMine
                  ? "Siz"
                  : message.senderName ||
                    senderUser.name ||
                    senderUser.email ||
                    "Kullanıcı";
              const messageReadBy = Array.isArray(message.readBy) ? message.readBy : [];
              const isReadByOther = isMine && (
                isGlobalSelected
                  ? messageReadBy.some((uid) => uid && uid !== currentUid)
                  : selectedUserId && messageReadBy.includes(selectedUserId)
              );
              return (
                <div
                  key={message.id}
                  style={{
                    display: "flex",
                    justifyContent: isMine ? "flex-end" : "flex-start",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12
                  }}
                >
                  {isMine && (
                    <MessageReactionSideButton
                      isDark={isDark}
                      onClick={() => {
                        setReactionMessageId((current) => current === message.id ? "" : message.id);
                        setActionMenuMessageId("");
                      }}
                    />
                  )}

                  <div
                    style={{
                      position: "relative",
                      maxWidth: "68%",
                      padding: isMine ? "11px 34px 11px 13px" : "11px 13px 11px 34px",
                      borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      background: isMine ? "#2563eb" : isDark ? "#1f2d42" : "#fff",
                      color: isMine ? "#fff" : isDark ? "#e5edf6" : "#0f172a",
                      border: isMine ? "none" : `1px solid ${isDark ? "#27364d" : "#e2e8f0"}`,
                      boxShadow: isMine ? "0 10px 20px rgba(37,99,235,0.18)" : "none"
                    }}
                  >
                    <button
                      data-chat-control="true"
                      type="button"
                      title="Mesaj seçenekleri"
                      onClick={() => {
                        setActionMenuMessageId((current) => current === message.id ? "" : message.id);
                        setReactionMessageId("");
                      }}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: isMine ? 7 : "auto",
                        left: isMine ? "auto" : 7,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: "none",
                        background: isMine
                          ? "rgba(255,255,255,0.16)"
                          : isDark
                          ? "rgba(15,23,42,0.55)"
                          : "#f1f5f9",
                        color: isMine ? "#fff" : isDark ? "#cbd5e1" : "#475569",
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: "22px",
                        padding: 0,
                        fontWeight: 950,
                        zIndex: 5
                      }}
                    >
                      ˅
                    </button>

                    {actionMenuMessageId === message.id && (
                      <MessageActionMenu
                        isMine={isMine}
                        isDark={isDark}
                        openUpward={shouldOpenMenuUpward}
                        onReply={() => startReply(message)}
                        onForward={() => startForward(message)}
                        onCopy={() => copyMessageText(message)}
                      />
                    )}

                    {reactionMessageId === message.id && (
                      <QuickReactionBar
                        isMine={isMine}
                        isDark={isDark}
                        onReact={(emoji) => handleToggleReaction(message, emoji)}
                      />
                    )}

                    {isGlobalSelected && (
                      <div
                        style={{
                          marginBottom: 6,
                          paddingBottom: 6,
                          borderBottom: `1px solid ${
                            isMine
                              ? "rgba(255,255,255,0.20)"
                              : isDark
                              ? "rgba(148,163,184,0.16)"
                              : "rgba(148,163,184,0.22)"
                          }`,
                          fontSize: Math.max(8, messageFontSize - 2),
                          fontWeight: 950,
                          color: isMine
                            ? "rgba(255,255,255,0.90)"
                            : isDark
                            ? "#cbd5e1"
                            : "#334155"
                        }}
                      >
                        <span>{senderName}</span>
                      </div>
                    )}

                    {(message.replyTo || message.forwardedFrom) && (
                      <div
                        style={{
                          marginBottom: 8,
                          padding: "7px 9px",
                          borderRadius: 10,
                          background: isMine
                            ? "rgba(255,255,255,0.14)"
                            : isDark
                            ? "rgba(15,23,42,0.42)"
                            : "#f1f5f9",
                          borderLeft: `3px solid ${isMine ? "#bfdbfe" : "#38bdf8"}`,
                          color: isMine ? "rgba(255,255,255,0.88)" : isDark ? "#cbd5e1" : "#475569"
                        }}
                      >
                        <div style={{ fontSize: Math.max(8, messageFontSize - 3), fontWeight: 950, marginBottom: 3 }}>
                          {message.forwardedFrom
                            ? `İletildi: ${message.forwardedFrom.senderName || "Kullanıcı"}`
                            : `Yanıt: ${message.replyTo.senderName || "Kullanıcı"}`}
                        </div>
                        <div
                          style={{
                            fontSize: Math.max(8, messageFontSize - 3),
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical"
                          }}
                        >
                          {(message.forwardedFrom || message.replyTo)?.text || ""}
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.45,
                        fontSize: messageFontSize
                      }}
                    >
                      {message.text}
                      <span
                        style={{
                          display: "inline-block",
                          marginLeft: 10,
                          fontSize: Math.max(7, messageFontSize - 3),
                          color: isMine ? "rgba(255,255,255,0.72)" : isDark ? "#94a3b8" : "#64748b",
                          fontWeight: 800,
                          whiteSpace: "nowrap",
                          verticalAlign: "baseline"
                        }}
                      >
                        {formatMessageTime(message.createdAt)}
                        {isMine && (
                          <span
                            title={isReadByOther ? "Okundu" : "Gönderildi"}
                            style={{
                              display: "inline-block",
                              marginLeft: 7,
                              color: isReadByOther ? "#7dd3fc" : "rgba(255,255,255,0.74)",
                              fontSize: Math.max(11, messageFontSize),
                              fontWeight: 950,
                              letterSpacing: -3,
                              opacity: 1,
                              verticalAlign: "baseline",
                              textShadow: isReadByOther ? "0 0 7px rgba(125,211,252,0.75)" : "none"
                            }}
                          >
                            ✓✓
                          </span>
                        )}
                      </span>
                    </div>

                    {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                      <div style={{ display: "grid", gap: 8, marginTop: message.text ? 9 : 0 }}>
                        {message.attachments.map((attachment, attachmentIndex) => (
                          attachment.isImage ? (
                            <a
                              key={`${attachment.url}-${attachmentIndex}`}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: "block",
                                overflow: "hidden",
                                borderRadius: 12,
                                border: `1px solid ${isMine ? "rgba(255,255,255,0.18)" : isDark ? "#334155" : "#e2e8f0"}`,
                                background: isMine ? "rgba(255,255,255,0.10)" : isDark ? "#0f172a" : "#f8fafc"
                              }}
                            >
                              <img
                                src={attachment.url}
                                alt={attachment.name || "Mesaj görseli"}
                                style={{
                                  display: "block",
                                  maxWidth: 280,
                                  maxHeight: 220,
                                  width: "100%",
                                  objectFit: "cover"
                                }}
                              />
                            </a>
                          ) : (
                            <a
                              key={`${attachment.url}-${attachmentIndex}`}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: "flex",
                                gap: 9,
                                alignItems: "center",
                                padding: "9px 10px",
                                borderRadius: 12,
                                border: `1px solid ${isMine ? "rgba(255,255,255,0.18)" : isDark ? "#334155" : "#e2e8f0"}`,
                                background: isMine ? "rgba(255,255,255,0.10)" : isDark ? "#0f172a" : "#f8fafc",
                                color: isMine ? "#fff" : isDark ? "#e5edf6" : "#124254",
                                textDecoration: "none",
                                fontWeight: 850
                              }}
                            >
                              <span style={{ fontSize: 18 }}>📎</span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                                  {attachment.name || "Dosya"}
                                </span>
                                <span style={{ display: "block", opacity: 0.72, fontSize: Math.max(8, messageFontSize - 3), marginTop: 2 }}>
                                  {formatFileSize(attachment.size)}
                                </span>
                              </span>
                            </a>
                          )
                        ))}
                      </div>
                    )}

                    {message.reactions && Object.keys(message.reactions).length > 0 && (
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
                        {Object.entries(message.reactions).map(([emoji, userIds]) => (
                          Array.isArray(userIds) && userIds.length > 0 ? (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => handleToggleReaction(message, emoji)}
                              style={{
                                border: "none",
                                borderRadius: 999,
                                padding: "3px 7px",
                                background: Array.isArray(userIds) && userIds.includes(currentUid)
                                  ? "#dbeafe"
                                  : isMine
                                  ? "rgba(255,255,255,0.18)"
                                  : isDark
                                  ? "#0f172a"
                                  : "#f1f5f9",
                                color: Array.isArray(userIds) && userIds.includes(currentUid)
                                  ? "#1d4ed8"
                                  : isMine
                                  ? "#fff"
                                  : isDark
                                  ? "#e5edf6"
                                  : "#334155",
                                fontWeight: 900,
                                cursor: "pointer"
                              }}
                            >
                              {emoji} {userIds.length}
                            </button>
                          ) : null
                        ))}
                      </div>
                    )}

                  </div>

                  {!isMine && (
                    <MessageReactionSideButton
                      isDark={isDark}
                      onClick={() => {
                        setReactionMessageId((current) => current === message.id ? "" : message.id);
                        setActionMenuMessageId("");
                      }}
                    />
                  )}
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </div>

          <form
            onSubmit={handleSendMessage}
            style={{
              display: "grid",
              gap: 10,
              padding: 16,
              borderTop: `1px solid ${isDark ? "#27364d" : "#e2e8f0"}`,
              background: isDark ? "#162235" : "#fff"
            }}
          >
            {replyingToMessage && (
              <ComposerInfoBox
                title={`${replyingToMessage.senderName || "Kullanıcı"} mesajına cevap`}
                text={replyingToMessage.text}
                isDark={isDark}
                onClose={() => setReplyingToMessage(null)}
              />
            )}

            {forwardingMessage && (
              <div
                style={{
                  display: "grid",
                  gap: 9,
                  padding: 11,
                  borderRadius: 14,
                  background: isDark ? "#0f172a" : "#f8fafc",
                  border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`
                }}
              >
                <ComposerInfoBox
                  title="Mesajı ilet"
                  text={forwardingMessage.text}
                  isDark={isDark}
                  onClose={() => {
                    setForwardingMessage(null);
                    setForwardTargetId("");
                  }}
                  embedded
                />

                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    value={forwardTargetId}
                    onChange={(event) => setForwardTargetId(event.target.value)}
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                      padding: "10px 11px",
                      background: isDark ? "#101827" : "#fff",
                      color: isDark ? "#e5edf6" : "#0f172a",
                      fontWeight: 800
                    }}
                  >
                    <option value="">Kime iletilecek?</option>
                    {forwardTargets.map((target) => (
                      <option key={target.uid} value={target.uid}>
                        {target.name || target.email}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={handleForwardMessage}
                    disabled={!forwardTargetId || isSending}
                    style={{
                      border: "none",
                      borderRadius: 12,
                      padding: "0 14px",
                      background: !forwardTargetId || isSending ? "#94a3b8" : "#0f766e",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: !forwardTargetId || isSending ? "not-allowed" : "pointer"
                    }}
                  >
                    İlet
                  </button>
                </div>
              </div>
            )}

            {selectedAttachments.length > 0 && (
              <div
                data-chat-control="true"
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  padding: 10,
                  borderRadius: 14,
                  background: isDark ? "#0f172a" : "#f8fafc",
                  border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`
                }}
              >
                {selectedAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      maxWidth: 220,
                      padding: attachment.isImage ? 4 : "8px 28px 8px 9px",
                      borderRadius: 12,
                      background: isDark ? "#182235" : "#ffffff",
                      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                      color: isDark ? "#e5edf6" : "#124254",
                      fontSize: 12,
                      fontWeight: 850
                    }}
                  >
                    {attachment.isImage ? (
                      <img
                        src={attachment.previewUrl}
                        alt={attachment.name}
                        style={{
                          width: 64,
                          height: 52,
                          borderRadius: 9,
                          objectFit: "cover"
                        }}
                      />
                    ) : (
                      <>
                        <span style={{ fontSize: 17 }}>📎</span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {attachment.name}
                          </span>
                          <span style={{ opacity: 0.65 }}>{formatFileSize(attachment.size)}</span>
                        </span>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => removeSelectedAttachment(attachment.id)}
                      disabled={isSending}
                      style={{
                        position: "absolute",
                        top: -7,
                        right: -7,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: "none",
                        background: "#ef4444",
                        color: "#fff",
                        cursor: isSending ? "not-allowed" : "pointer",
                        fontWeight: 950,
                        lineHeight: "22px",
                        padding: 0
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                  onChange={handleAttachmentSelect}
                  style={{ display: "none" }}
                />

                {isComposerEmojiOpen && (
                  <div
                    data-chat-control="true"
                    style={{
                      position: "absolute",
                      right: 8,
                      bottom: 62,
                      display: "flex",
                      gap: 6,
                      padding: "8px 9px",
                      borderRadius: 999,
                      background: isDark ? "#1e293b" : "#ffffff",
                      border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                      boxShadow: "0 16px 36px rgba(15,23,42,0.18)",
                      zIndex: 20
                    }}
                  >
                    {COMPOSER_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => appendEmojiToComposer(emoji)}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          border: "none",
                          background: isDark ? "#0f172a" : "#f8fafc",
                          cursor: "pointer",
                          fontSize: 17
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}

                <textarea
                  ref={messageInputRef}
                  value={messageText}
                  onChange={(event) => setMessageText(event.target.value)}
                  disabled={!selectedUser || isSending}
                  placeholder={selectedUser ? "Mesajınızı yazın..." : "Önce kullanıcı seçin"}
                  rows={2}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "none",
                    borderRadius: 14,
                    border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                    padding: "11px 82px 11px 12px",
                    outline: "none",
                    background: isDark ? "#0f172a" : "#fff",
                    color: isDark ? "#e5edf6" : "#0f172a",
                    fontWeight: 700,
                    fontSize: messageFontSize
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSendMessage(event);
                    }
                  }}
                />

                <button
                  data-chat-control="true"
                  type="button"
                  title="Dosya veya resim ekle"
                  disabled={!selectedUser || isSending || selectedAttachments.length >= MAX_CHAT_ATTACHMENTS}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    position: "absolute",
                    right: 46,
                    bottom: 12,
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: isDark ? "#1e293b" : "#f1f5f9",
                    color: isDark ? "#e5edf6" : "#475569",
                    cursor: !selectedUser || isSending || selectedAttachments.length >= MAX_CHAT_ATTACHMENTS ? "not-allowed" : "pointer",
                    opacity: !selectedUser || isSending || selectedAttachments.length >= MAX_CHAT_ATTACHMENTS ? 0.55 : 1,
                    fontSize: 16
                  }}
                >
                  📎
                </button>

                <button
                  data-chat-control="true"
                  type="button"
                  title="Emoji ekle"
                  disabled={!selectedUser || isSending}
                  onClick={() => setIsComposerEmojiOpen((current) => !current)}
                  style={{
                    position: "absolute",
                    right: 10,
                    bottom: 12,
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "none",
                    background: isDark ? "#1e293b" : "#f1f5f9",
                    color: isDark ? "#e5edf6" : "#475569",
                    cursor: !selectedUser || isSending ? "not-allowed" : "pointer",
                    opacity: !selectedUser || isSending ? 0.55 : 1,
                    fontSize: 17
                  }}
                >
                  ☺
                </button>
              </div>

              <button
                type="submit"
                disabled={!selectedUser || (!messageText.trim() && selectedAttachments.length === 0) || isSending}
                style={{
                  width: 54,
                  borderRadius: 14,
                  border: "none",
                  background:
                    !selectedUser || (!messageText.trim() && selectedAttachments.length === 0) || isSending
                      ? "#94a3b8"
                      : "#2563eb",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor:
                    !selectedUser || (!messageText.trim() && selectedAttachments.length === 0) || isSending
                      ? "not-allowed"
                      : "pointer"
                }}
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function MessageReactionSideButton({ isDark, onClick }) {
  return (
    <button
      data-chat-control="true"
      type="button"
      title="İfade bırak"
      onClick={onClick}
      style={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        border: "none",
        background: isDark ? "#1e293b" : "#e2e8f0",
        color: isDark ? "#cbd5e1" : "#64748b",
        cursor: "pointer",
        fontSize: 16,
        flex: "0 0 30px",
        boxShadow: isDark ? "none" : "0 8px 18px rgba(15,23,42,0.10)"
      }}
    >
      ☺
    </button>
  );
}

function MessageActionMenu({ isMine, isDark, openUpward = false, onReply, onForward, onCopy }) {
  const itemStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    border: "none",
    background: "transparent",
    color: isDark ? "#e5edf6" : "#1f2937",
    padding: "10px 12px",
    textAlign: "left",
    fontSize: 14,
    fontWeight: 850,
    cursor: "pointer"
  };

  return (
    <div
      data-chat-control="true"
      style={{
        position: "absolute",
        top: openUpward ? "auto" : 32,
        bottom: openUpward ? 32 : "auto",
        right: isMine ? 4 : "auto",
        left: isMine ? "auto" : 4,
        width: 176,
        borderRadius: 16,
        overflow: "hidden",
        background: isDark ? "rgba(30,41,59,0.98)" : "rgba(255,255,255,0.98)",
        border: `1px solid ${isDark ? "#334155" : "#dbe2ea"}`,
        boxShadow: "0 18px 44px rgba(15,23,42,0.24)",
        zIndex: 30
      }}
    >
      <button type="button" onClick={onReply} style={itemStyle}>
        <span>↩</span>
        <span>Cevapla</span>
      </button>
      <button type="button" onClick={onForward} style={itemStyle}>
        <span>↪</span>
        <span>İlet</span>
      </button>
      <button type="button" onClick={onCopy} style={{ ...itemStyle, borderTop: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
        <span>⧉</span>
        <span>Kopyala</span>
      </button>
    </div>
  );
}

function QuickReactionBar({ isMine, isDark, onReact }) {
  return (
    <div
      data-chat-control="true"
      style={{
        position: "absolute",
        top: -42,
        right: isMine ? 0 : "auto",
        left: isMine ? "auto" : 0,
        display: "flex",
        gap: 7,
        padding: "8px 10px",
        borderRadius: 999,
        background: isDark ? "rgba(30,41,59,0.98)" : "rgba(255,255,255,0.98)",
        border: `1px solid ${isDark ? "#334155" : "#dbe2ea"}`,
        boxShadow: "0 16px 36px rgba(15,23,42,0.20)",
        zIndex: 25
      }}
    >
      {QUICK_REACTIONS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onReact(emoji)}
          style={{
            width: 31,
            height: 31,
            borderRadius: "50%",
            border: "none",
            background: isDark ? "#0f172a" : "#f8fafc",
            cursor: "pointer",
            fontSize: 18
          }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function ComposerInfoBox({ title, text, isDark, onClose, embedded = false }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: embedded ? 0 : 10,
        borderRadius: 12,
        background: embedded ? "transparent" : isDark ? "#0f172a" : "#f8fafc",
        border: embedded ? "none" : `1px solid ${isDark ? "#334155" : "#cbd5e1"}`
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: isDark ? "#bfdbfe" : "#1d4ed8", fontWeight: 950, fontSize: 12, marginBottom: 3 }}>
          {title}
        </div>
        <div
          style={{
            color: isDark ? "#cbd5e1" : "#475569",
            fontWeight: 750,
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {text}
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        style={{
          border: "none",
          borderRadius: 999,
          width: 24,
          height: 24,
          background: isDark ? "#1e293b" : "#e2e8f0",
          color: isDark ? "#e5edf6" : "#334155",
          cursor: "pointer",
          fontWeight: 950
        }}
      >
        ×
      </button>
    </div>
  );
}

export default MessagesView;
